import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { colors, radius, spacing, type, useAppTheme } from '@/theme';
import { ScaledSheet, useResponsive } from '@/theme/responsive';

export interface TimeSlot {
  time: string;
  available: boolean;
}

interface Group {
  label: string;
  slots: TimeSlot[];
}

interface Props {
  groups: Group[];
  selected: string | null;
  onSelect: (time: string) => void;
}

export function TimeSlotSelector({ groups, selected, onSelect }: Props) {
  const { colors } = useAppTheme();
  // An even grid, so the times line up and none runs wide: three across, two on a compact screen
  // (narrow, or a large system font) where three would squeeze the numbers.
  const { isCompact } = useResponsive();
  const columnWidth = isCompact ? '48%' : '31.5%';
  return (
    <View style={styles.container}>
      {groups.map((group) => (
        <View key={group.label} style={styles.group}>
          <Text style={[type.overline, styles.groupLabel, { color: colors.textTertiary }]}>{group.label.toUpperCase()}</Text>
          <View style={styles.grid}>
            {group.slots.map((slot) => {
              const isSelected = slot.time === selected;
              return (
                <Pressable
                  key={slot.time}
                  disabled={!slot.available}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected, disabled: !slot.available }}
                  onPress={() => onSelect(slot.time)}
                  style={[
                    styles.slot,
                    { width: columnWidth },
                    { backgroundColor: colors.surface, borderColor: colors.border },
                    isSelected && styles.slotSelected,
                    !slot.available && [styles.slotDisabled, { backgroundColor: colors.surfaceMuted, borderColor: colors.surfaceMuted }],
                  ]}>
                  <Text
                    numberOfLines={1}
                    adjustsFontSizeToFit
                    minimumFontScale={0.85}
                    style={[
                      type.bodyMedium,
                      isSelected
                        ? styles.textSelected
                        : slot.available
                          ? [styles.textAvailable, { color: colors.textPrimary }]
                          : [styles.textDisabled, { color: colors.textTertiary }],
                    ]}>
                    {slot.time}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      ))}
    </View>
  );
}

const styles = ScaledSheet.create({
  container: { gap: spacing.lg },
  group: { gap: spacing.sm },
  groupLabel: { color: colors.textTertiary },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  slot: {
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: 12,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  slotSelected: { backgroundColor: colors.accentFill, borderColor: colors.accent },
  slotDisabled: { backgroundColor: colors.surfaceMuted, borderColor: colors.surfaceMuted },
  textAvailable: { color: colors.textPrimary },
  textSelected: { color: colors.textOnAccent },
  textDisabled: { color: colors.textTertiary, textDecorationLine: 'line-through' },
});
